import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export interface SendOtpResponse {
  success: boolean;
  message: string;
  expiresIn?: number;
}

export interface VerifyOtpResponse {
  success: boolean;
  token?: string;
  message?: string;
}

const sendOtpFn = httpsCallable(functions, 'sendOtp');
const verifyOtpFn = httpsCallable(functions, 'verifyOtp');
const checkUserExistsFn = httpsCallable(functions, 'checkUserExists');

export async function requestOtp(phone: string): Promise<SendOtpResponse> {
  try {
    const res: any = await sendOtpFn({ phone });
    return res.data as SendOtpResponse;
  } catch (error: any) {
    // Extract error message from Firebase error structure
    let errorMessage = 'Failed to send OTP. Please try again.';
    
    if (error?.code) {
      switch (error.code) {
        case 'resource-exhausted':
          errorMessage = error.message || 'Too many OTP requests. Please wait before requesting again.';
          break;
        case 'invalid-argument':
          errorMessage = error.message || 'Invalid phone number. Please enter a valid 10-digit number.';
          break;
        case 'failed-precondition':
          errorMessage = error.message || 'OTP service is temporarily unavailable. Please try again after some time or contact support.';
          break;
        case 'internal':
          errorMessage = error.message || 'Failed to send OTP. Please try again later.';
          break;
        default:
          errorMessage = error.message || error.details || 'Failed to send OTP. Please try again.';
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    return { success: false, message: errorMessage };
  }
}

export async function verifyOtp(phone: string, otp: string): Promise<VerifyOtpResponse> {
  try {
    const res: any = await verifyOtpFn({ phone, otp, appType: 'customer' });
    return res.data as VerifyOtpResponse;
  } catch (error: any) {
    // Extract error message from Firebase error structure
    let errorMessage = 'OTP verification failed. Please try again.';
    
    if (error?.code) {
      switch (error.code) {
        case 'permission-denied':
          // This includes conflict errors (cross-account type)
          errorMessage = error.message || 'This phone number is registered with a different account type. Please use the correct app.';
          break;
        case 'deadline-exceeded':
          errorMessage = error.message || 'OTP expired. Please request a new one.';
          break;
        case 'resource-exhausted':
          errorMessage = error.message || 'Maximum verification attempts exceeded. Please request a new OTP.';
          break;
        case 'not-found':
          errorMessage = error.message || 'No OTP requested for this number. Please request a new OTP.';
          break;
        case 'invalid-argument':
          errorMessage = error.message || 'Invalid OTP. Please check and try again.';
          break;
        default:
          errorMessage = error.message || error.details || 'OTP verification failed. Please try again.';
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    // Return error response instead of throwing
    return { success: false, message: errorMessage };
  }
}


export async function checkUserExists(phone: string): Promise<{ exists: boolean; message: string; accountType?: string; conflict?: boolean }> {
  try {
    const res: any = await checkUserExistsFn({ phone, appType: 'customer' });
    return res.data as { exists: boolean; message: string; accountType?: string; conflict?: boolean };
  } catch (error: any) {
    // If the function fails, assume user doesn't exist to allow signup
    return { exists: false, message: 'Unable to check user existence', conflict: false };
  }
}


